import {
  EXPANDABLE_DETAILS_KEY,
  FIELD_DEFAULTS_GENERATOR_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaV2NestedShapeValues,
  SHOW_DIVIDER_LINE_KEY,
  StepConfigEditorOverride,
} from '../../StepConfigEditorTypes';
import {isSeriesColumnNameNull} from '../StepConfigDisableFieldFunctions';
import {getDefaultSeriesColorConfig, onChangeDiscreteColorPaletteMiddleware} from '../StepConfigUISchemas';

/**
 * Bar Chart configuration sections for the Configure tab
 */
export const BAR_CHART_CONFIG_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'barChartTypeSection',
    'axesSection',
    'errorBarsSection',
    'seriesAndColorPaletteSection',
  ],
  barChartTypeSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['barChartType'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    barChartType: {
      displayName: 'Type',
      configShapePath: ['chartConfig', 'parameters', 'barChartType'],
      editorOverride: StepConfigEditorOverride.ICON_SELECTOR,
      fieldSettings: {
        displayNames: {
          STANDARD: 'Standard',
          STACKED: 'Stacked',
        },
        svgImagePaths: {
          STANDARD: '/static/img/insights/grouped_bar_chart.svg',
          STACKED: '/static/img/insights/stacked_bar_chart.svg',
        },
      },
    },
  },
  axesSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisGroup', 'yAxisGroup'],
    xAxisGroup: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['xAxisColumnName', 'xAxisLabel'],
      [EXPANDABLE_DETAILS_KEY]: {
        headerField: 'xAxisColumnName',
        detailFields: ['xAxisLabel'],
      },
      xAxisColumnName: {
        displayName: 'X-axis',
        configShapePath: ['chartConfig', 'parameters', 'xAxisColumnName'],
      },
      xAxisLabel: {
        displayName: 'Custom axis label',
        configShapePath: ['chartConfig', 'parameters', 'xAxisLabel'],
      },
    },
    yAxisGroup: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisColumnName', 'yAxisLabel', 'yAxisScale'],
      [EXPANDABLE_DETAILS_KEY]: {
        headerField: 'yAxisColumnName',
        detailFields: ['yAxisLabel', 'yAxisScale'],
      },
      yAxisColumnName: {
        displayName: 'Y-axis',
        configShapePath: ['chartConfig', 'parameters', 'yAxisColumnName'],
      },
      yAxisLabel: {
        displayName: 'Custom axis label',
        configShapePath: ['chartConfig', 'parameters', 'yAxisLabel'],
      },
      yAxisScale: {
        displayName: 'Scale',
        configShapePath: ['chartConfig', 'parameters', 'yAxisScale'],
        fieldSettings: {
          displayNames: {
            LINEAR: 'Linear',
            LOG: 'Log',
          },
        },
      },
    },
  },
  seriesAndColorPaletteSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesSection', 'colorSection'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Series & Color Palette',
    seriesSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['seriesColumnName'],
      seriesColumnName: {
        displayName: 'Series',
        configShapePath: ['chartConfig', 'parameters', 'seriesColumnName'],
        fieldSettings: {
          allowSelectNull: true,
          noColumnSelectedText: 'None',
        },
      },
    },
    colorSection: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorConfigSection', 'seriesColorConfigsSection'],
      [EXPANDABLE_DETAILS_KEY]: {
        headerField: 'colorConfigSection',
        detailFields: ['seriesColorConfigsSection'],
      },
      [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeDiscreteColorPaletteMiddleware,
      colorConfigSection: {
        displayName: 'Color palette',
        configShapePath: ['chartConfig', 'parameters', 'discreteColorPalette'],
        fieldSettings: {
          defaultValue: 'BENCHLING',
          displayNames: {
            BENCHLING: 'Benchling (30)',
            DARK24: 'Dark (24)',
            LIGHT24: 'Light (24)',
            BOLD: 'Bold (11)',
            PASTEL: 'Pastel (11)',
            SAFE: 'Safe (11)',
            PRISM: 'Crystal (11)',
          },
          svgImagePaths: {
            BENCHLING: '/static/img/insights/palettes/benchling.svg',
            DARK24: '/static/img/insights/palettes/dark24.svg',
            LIGHT24: '/static/img/insights/palettes/light24.svg',
            BOLD: '/static/img/insights/palettes/bold.svg',
            PASTEL: '/static/img/insights/palettes/pastel.svg',
            SAFE: '/static/img/insights/palettes/safe.svg',
            PRISM: '/static/img/insights/palettes/prism.svg',
          },
        },
      },
      seriesColorConfigsSection: {
        configShapePath: ['chartConfig', 'parameters', 'seriesColorConfigs'],
        editorOverride: StepConfigEditorOverride.MAPPING_ARRAY,
        fieldSettings: {
          mappingSourcePath: ['chartConfig', 'parameters', 'seriesColumnName'],
          mappingKey: 'series',
          showNoMappingEditor: true,
          noMappingDisplayName: 'Base color',
          noMappingSentinel: '__BENCHLING_NO_SERIES_DEFAULT__',
        },
        elementUISchema: {
          [FIELD_ORDER_UI_SCHEMA_KEY]: ['series', 'color'],
          [FIELD_DEFAULTS_GENERATOR_KEY]: getDefaultSeriesColorConfig,
          series: {
            configShapePath: ['series'],
            disableFn: (): boolean => true,
            editorOverride: StepConfigEditorOverride.STRING_LABEL,
          },
          color: {
            configShapePath: ['color'],
            editorOverride: StepConfigEditorOverride.COLOR_PICKER,
          },
        },
      },
    },
  },
  errorBarsSection: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['errorBarColumnName'],
    errorBarColumnName: {
      displayName: 'Error bar',
      configShapePath: ['chartConfig', 'parameters', 'errorBarColumnName'],
      fieldSettings: {
        allowSelectNull: true,
        noColumnSelectedText: 'None',
      },
    },
  },
} as const satisfies ShapeUISchemaV2NestedShapeValues;

/**
 * Bar Chart style sections for the Style tab
 */
export const BAR_CHART_STYLE_SECTIONS = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['legendSection', 'yAxisSection'],
  legendSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Legend',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['showLegend', 'position', 'customLabel'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    showLegend: {
      configShapePath: ['chartConfig', 'parameters', 'showLegend'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Show legend',
        defaultValue: true,
      },
      disableFn: isSeriesColumnNameNull,
    },
    position: {
      displayName: 'Position',
      configShapePath: ['chartConfig', 'parameters', 'legendLocation'],
      disableFn: isSeriesColumnNameNull,
    },
    customLabel: {
      displayName: 'Custom label (optional)',
      configShapePath: ['chartConfig', 'parameters', 'legendLabel'],
      disableFn: isSeriesColumnNameNull,
    },
  },
  yAxisSection: {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Y-axis',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisRange'],
    yAxisRange: {
      [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisMin', 'yAxisMax'],
      [NUM_COLUMNS_KEY]: 2,
      yAxisMin: {
        displayName: 'Min',
        configShapePath: ['chartConfig', 'parameters', 'yAxisMin'],
      },
      yAxisMax: {
        displayName: 'Max',
        configShapePath: ['chartConfig', 'parameters', 'yAxisMax'],
      },
    },
  },
} as const satisfies ShapeUISchemaV2NestedShapeValues;
